
import { Component, Vue } from "vue-property-decorator";
import Widget from "@/components/dashboard/Widget.vue";
import WidgetData from "@/interfaces/dashboard/WidgetData";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiPlusCircleOutline } from "@mdi/js";
import { Getter } from "vuex-class";
import DashboardData from "@/interfaces/dashboard/DashboardData";

@Component({
  components: {
    SvgIcon,
    Widget,
  },
})

export default class Dashboard extends Vue {

  @Getter private currentUser: any;
  @Getter private envCompanyId: any;

  private dashboardList: DashboardData[] = [];
  private selectedDashboard: DashboardData | null = null;
  private dashboardLoading: boolean = false;
  private widgetList: WidgetData[] = [];
  private widgetTypeList: string[] = [];
  private selectedWidgetType: string | null = null;
  private mdiPlusCircleOutline = mdiPlusCircleOutline;


  /**
   * Returns a sorted set of vertical positions of widgets from the widget list.
   *
   * @return {Set} - A sorted set of vertical positions.
   */
  get widgetListRows() {
    return new Set(this.widgetList.map((w) => w.verticalPosition).sort((a, b) => a - b));
  }


  /**
   * Returns a filtered list of widget types.
   *
   * @returns {Array} - The filtered widget type list.
   */
  get widgetTypeListFiltered() {
    return this.widgetTypeList.filter(
      (type) => !this.widgetList.find((widget) => widget.type === type && widget.frontEndState !== "DELETED"),
    );
  }


  /**
   * Mounts the component.
   *
   * @private
   * @async
   * @returns {Promise<void>} - A promise that resolves when the component is mounted.
   */
  private async mounted() {
    await this.reset();
  }


  /**
   * Reset method.
   *
   * This method resets the state of the object by performing the following steps:
   * 1. If the currentUser and envCompanyId exist:
   *    - Call the getDashboardByUserId method with the currentUser.id and envCompanyId as parameters.
   *    - Set the selectedDashboard to the first dashboard in dashboardList.
   *    - Call the getWidgetsByDashboardId method with the selectedDashboard.id as a parameter.
   * 2. Call the getWidgetTypeList method.
   * 3. Set the selectedWidgetType to the first widget type in widgetTypeList.
   *
   * @returns {Promise<void>} A Promise that resolves when the reset operation is complete.
   */
  private async reset() {
    if (this.currentUser && this.envCompanyId) {
      await this.getDashboardByUserId(this.currentUser.id, this.envCompanyId);
      // for the moment only use one dashboard
      if (this.dashboardList[0]) {
        this.selectedDashboard = this.dashboardList[0];
        await this.getWidgetsByDashboardId(this.selectedDashboard.id);
      }
      await this.getWidgetTypeList();
      this.selectedWidgetType = this.widgetTypeList[0];
    }
  }


  /**
   * Returns a sorted list of widgets filtered by the provided row.
   *
   * @param {number} row - The row number to filter the widgets by.
   * @return {Array} - The sorted list of widgets per row.
   */
  private widgetListPerRow(row) {
    return this.widgetList
      .filter((w) => w.verticalPosition === row && w.frontEndState !== "DELETED")
      .sort((a, b) =>
        (a.verticalPosition * 12 + a.horizontalPosition) -
        (b.verticalPosition * 12 + b.horizontalPosition),
      );

  }


  /**
   * Creates a new dashboard for the user.
   *
   * @returns {Promise<void>} - A promise that resolves when the dashboard has been created successfully.
   */
  private createDashboard(): Promise<void> {
    if (this.currentUser && this.envCompanyId) {
      this.dashboardLoading = true;
      return this.$store.dispatch("dashboards/createDashboard",
        { userId: this.currentUser.id, companyId: this.envCompanyId },
      )
        .then(async () => {
          await this.getDashboardByUserId(this.currentUser.id, this.envCompanyId);
          this.selectedDashboard = this.dashboardList[0];

        });
    }
  }


  /**
   * Fetches the list of widget types from the store.
   *
   * @returns {Promise<void>} A promise that resolves when the widget type list is fetched successfully.
   */
  private getWidgetTypeList(): Promise<void> {
    return this.$store.dispatch("widgets/getWidgetTypeList")
      .then((data) => {
        this.widgetTypeList = data;
      });
  }


  /**
   * Creates a widget by dispatching the 'createWidget' action to the store.
   * Calls the 'getWidgetsByDashboardId' method to update the widgets for the selected dashboard.
   *
   * @returns A promise that resolves to void.
   */
  private createWidget(): Promise<void> {
    if (this.selectedWidgetType && this.selectedDashboard.id) {
      return this.$store.dispatch("dashboards/createWidget", {
        dashboardId: this.selectedDashboard.id,
        widgetType: this.selectedWidgetType,
      })
        .then(() => {
          return this.getWidgetsByDashboardId(this.selectedDashboard.id);
        });
    }
  }


  /**
   * Fetches dashboard list by user id and company id.
   *
   * @param {number} userId - The user id.
   * @param {number} companyId - The company id.
   * @return {Promise<void>} - A promise that resolves with nothing when the dashboard list is fetched successfully.
   */
  private getDashboardByUserId(userId, companyId): Promise<void> {
    this.dashboardLoading = true;
    return this.$store.dispatch("dashboards/getDashboardsByUserId", {
      params: { userId, companyId },
    }).then((res) => {
      this.dashboardList = res;
    }).finally(() => {
      this.dashboardLoading = false;
    });
  }


  /**
   * Retrieves the list of widgets associated with a specific dashboard.
   *
   * @param {number} dashboardId - The identifier of the dashboard to retrieve widgets for.
   * @return {Promise<void>} - A Promise that resolves when the widgets have been retrieved and processed successfully.
   */
  private getWidgetsByDashboardId(dashboardId): Promise<void> {
    return this.$store.dispatch("dashboards/getWidgetsByDashboardId", {
      dashboardId,
      params: {},
    }).then((res) => {
      this.widgetList = res;
      this.widgetList.forEach((widget) => widget.frontEndState = "PRE_LOADING");
      return this.fetchWidgetData(this.selectedDashboard.id);
    });
  }


  /**
   * Fetches widget data for a given dashboard id.
   *
   * @param {*} dashboardId - The id of the dashboard.
   * @returns {Promise<void>} - A promise that resolves with no value.
   */
  private async fetchWidgetData(dashboardId) {
    for (const index in this.widgetList) {
      if (this.widgetList.hasOwnProperty(index)) {
        this.$set(this.widgetList, index, { ...this.widgetList[index], frontEndState: "LOADING" });
        await this.getWidgetById(dashboardId, this.widgetList[index].id)
          .then((data) => {
            if (
              data !== null &&
              typeof data === "object" &&
              this.widgetList[index] &&
              typeof this.widgetList[index] === "object"
            ) {
              // @ts-ignore
              this.$set(this.widgetList, index, { ...this.widgetList[index], ...data, frontEndState: "LOADED" });
            }
          })
          .catch((error) => {
            this.$set(this.widgetList, index, { ...this.widgetList[index], frontEndState: "ERROR" });
          });
      }
    }
  }


  /**
   * Retrieves a widget by its ID from the store.
   *
   * @param {number} dashboardId - The ID of the dashboard.
   * @param {number} widgetId - The ID of the widget.
   * @returns {Promise<void>} - A promise that resolves when the widget is retrieved.
   */
  private getWidgetById(dashboardId: number, widgetId: number): Promise<void> {
    return this.$store.dispatch("widgets/getById", {
      dashboardId,
      widgetId,
      params: {
        companyId: this.envCompanyId,
      },
    });
  }
}
